import { useEffect, useId, useState } from 'react';
import { CheckIcon, CloseIcon, EyeIcon, EyeOffIcon } from './icons.js';

/**
 * Password input with a show/hide toggle and, when `minLength` is set, a live
 * requirement line that states the rule from first render instead of only after
 * a failed submit. Shared by registration, reset-password and the profile page,
 * so it renders its own label markup rather than either `Field` — the
 * registration one is registration-only and ProfileView's local one has no
 * error support.
 *
 * The requirement line and the error render OUTSIDE the <label> deliberately:
 * Testing Library derives getByLabelText from the label's text content, and
 * ProfileView's tests query the anchored /^new password$/i. Text inside the
 * label would break those queries. The toggle button may stay inside because
 * its only content is an aria-hidden svg, which contributes no text.
 *
 * `data-met` on the requirement line is the state hook tests assert against, so
 * they don't depend on Tailwind classes.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  error,
  minLength,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  error?: string;
  minLength?: number;
  required?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const met = minLength !== undefined && value.length >= minLength;
  const baseId = useId();
  const requirementId = `${baseId}-requirement`;
  const errorId = `${baseId}-error`;
  const describedBy =
    [minLength !== undefined ? requirementId : null, error ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  useEffect(() => {
    if (value === '') setVisible(false);
  }, [value]);

  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-2">
        <span className="field-label">{label}</span>
        <div className="relative">
          <input
            className="input pr-11"
            type={visible ? 'text' : 'password'}
            autoComplete={autoComplete}
            minLength={minLength}
            required={required}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
          />
          <button
            type="button"
            aria-label={visible ? 'Hide password' : 'Show password'}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-500
                       hover:text-ink-800 dark:hover:text-ink-200 transition"
            onClick={() => setVisible((v) => !v)}
          >
            {visible ? <EyeOffIcon width={18} height={18} /> : <EyeIcon width={18} height={18} />}
          </button>
        </div>
      </label>

      {minLength !== undefined ? (
        <>
          <span
            id={requirementId}
            data-met={met ? 'true' : 'false'}
            className={
              met
                ? 'inline-flex items-center gap-1.5 text-[0.8rem] text-status-success'
                : 'hint inline-flex items-center gap-1.5 text-[0.8rem]'
            }
          >
            {met ? <CheckIcon width={14} height={14} /> : <CloseIcon width={14} height={14} />}
            At least {minLength} characters
          </span>
          <span className="sr-only" aria-live="polite">
            {met ? `Password meets the ${minLength} character minimum` : ''}
          </span>
        </>
      ) : null}

      {error ? (
        <span id={errorId} className="text-[0.8rem] text-status-error dark:text-red-400">
          {error}
        </span>
      ) : null}
    </div>
  );
}
